import angularModule from './student_network_module';
import debounce from 'Utils/debounce';

angularModule.factory('StudentNetworkMapViewModel', [
    '$injector',

    function factory($injector) {
        const SuperModel = $injector.get('SuperModel');
        const StudentsMapLayer = $injector.get('StudentsMapLayer');
        const EventsMapLayer = $injector.get('EventsMapLayer');
        const $window = $injector.get('$window');
        const GoogleOverlayView = $injector.get('GoogleOverlayView');
        const guid = $injector.get('guid');
        const $timeout = $injector.get('$timeout');
        const Cohort = $injector.get('Cohort');
        const DialogModal = $injector.get('DialogModal');
        const reactRouterBridge = $injector.get('reactRouterBridge');
        const StudentNetworkEvent = $injector.get('StudentNetworkEvent');
        const $q = $injector.get('$q');

        let googleMaps;

        const StudentNetworkMapViewModel = SuperModel.subclass(function () {
            this.extend({
                // There are some things that cannot be initialized until
                // the google maps api is loaded.  network-map-dir is responsible
                // for doing this before initializing a StudentNetworkMapViewModel
                onGoogleApiLoaded() {
                    if (!this.ranOnGoogleApiLoaded) {
                        GoogleOverlayView.onGoogleApiLoaded();
                        this.ranOnGoogleApiLoaded = true;
                        googleMaps = $window.google.maps;
                    }
                },
            });

            Object.defineProperty(this.prototype, 'loadingData', {
                get() {
                    return this.dataLayers.some(layer => layer?.loading);
                },
            });

            return {
                initialize(map, userCohortIds, eventId, showEvents) {
                    if (!StudentNetworkMapViewModel.ranOnGoogleApiLoaded) {
                        throw new Error('onGoogleApiLoaded has not yet been called.');
                    }

                    this.map = map;
                    this._id = guid.generate();
                    this.eventId = eventId;
                    this.showEvents = showEvents;
                    const location = reactRouterBridge.search();
                    this.initialSearchKeyword = location.keyword;
                    this.showFeaturedEvents = location['show-featured-events'];
                    this.myClass = location.myClass;
                    this.byIds = location.ids;
                    this.userCohortIds = userCohortIds;

                    this.studentsMapLayer = new StudentsMapLayer(map);
                    this.eventsMapLayer = new EventsMapLayer({
                        map,
                        studentNetworkMapViewModel: this,
                        userCohortIds: this.userCohortIds,
                    });
                    this._listeners = [
                        googleMaps.event.addListener(map, 'idle', debounce(this._onIdleEvent.bind(this), 500)),
                    ];
                    $($window).on(`resize.${this._id}`, this._updateMapSize.bind(this));

                    this.dataLayers = [this.studentsMapLayer, this.eventsMapLayer];
                    // initially fetch and render the data
                    $timeout(() => {
                        this.setDefaultActiveLayer();
                    }, 500);
                },

                setDefaultActiveLayer() {
                    // If the user navigated to the student network via a URL
                    // with an `event-id` query parameter (passed in through
                    // reactRouteProvider), then we need to set the default layer
                    // to the "events" layer, and kick off the process of fetching
                    // and showing that particular event'd details.
                    if (this.eventId || this.showEvents || this.showFeaturedEvents) {
                        this.setActiveMapLayer(this.eventsMapLayer);
                        if (this.eventId) {
                            this.fetchEventAndShowDetails();
                        }
                        if (this.showFeaturedEvents) {
                            this.activeMapLayer.currentRecommendedEventsListTab = 'featured';
                            this.activeMapLayer.fetchFeaturedEvents();
                        }
                    } else {
                        // Apply initial search, if there is one, before activating layer so that map bubbles update when activated
                        this.applyInitialSearch();
                        this.setActiveMapLayer(this.studentsMapLayer);
                    }
                },

                setActiveMapLayer(layer) {
                    if (this.activeMapLayer === layer) {
                        return;
                    }
                    this.activeMapLayer = layer;
                    this.activeMapLayer.activate();
                    this.activeMapLayer.refresh();
                    this.dataLayers
                        .filter(layer => layer !== this.activeMapLayer)
                        .forEach(layer => layer?.deactivate());
                },

                applyInitialSearch() {
                    if (!this.initialSearchKeyword && !this.byIds && !this.myClass) return;

                    if (this.initialSearchKeyword) {
                        this.studentsMapLayer.advancedFilters.keyword_search = this.initialSearchKeyword;
                        this.studentsMapLayer.inAdvancedSearchMode = true;
                    }
                    if (this.byIds) {
                        this.studentsMapLayer.advancedFilters.user_id = this.byIds;
                    }
                    if (this.myClass) {
                        this.studentsMapLayer.advancedFilters.class = ['mine'];
                    }
                    this.studentsMapLayer.inAdvancedSearchMode = true;
                    this.studentsMapLayer.applyAdvancedFilters();
                },

                updateMyClassCohortId(currentUser) {
                    const cohort = currentUser?.cohortForActiveProgramInclusion;

                    // Note: if you deferred into a cohort that hasn't yet reached its registration deadline,
                    // we don't want you to be able to filter by My Class and potentially very few
                    // people in the network
                    // This could change if we changed our policy of accepting everybody at once.
                    if (
                        currentUser &&
                        currentUser.hasAccessToFullStudentNetworkProfiles &&
                        cohort &&
                        Cohort.canFilterNetworkForMyClass(cohort.program_type) &&
                        cohort.registrationDeadlineHasPassed
                    ) {
                        this.dataLayers.forEach(layer => {
                            layer.myClassCohortId = cohort.id;
                        });
                    } else {
                        this.dataLayers.forEach(layer => {
                            layer.myClassCohortId = null;
                        });
                    }
                },

                destroy() {
                    this.dataLayers.forEach(layer => layer?.deactivate());
                    this._listeners.forEach(listener => listener?.remove());
                    $($window).off(`resize.${this._id}`);
                    DialogModal.hideAlerts();
                },

                //--------------------
                // Event details
                //--------------------

                fetchEventAndShowDetails() {
                    this.fetchEventForId(this.eventId)
                        .then(response => {
                            if (response.result[0]) {
                                this.showEventDetailsModal(response.result[0]);
                                return $q.resolve();
                            }
                            return $q.reject();
                        })
                        .catch(() => {
                            reactRouterBridge.search('event-id', null);
                        });
                },

                fetchEventForId(eventId) {
                    return StudentNetworkEvent.index({
                        filters: {
                            id: eventId,
                            start_time: 0,
                            end_time: new Date('2099/01/01').getTime(),
                            include_tbd: true,
                            cohort_ids: this.userCohortIds,
                        },
                    })
                        .then(response => $q.resolve(response))
                        .catch(() => $q.reject());
                },

                showEventDetailsModal(event) {
                    if (!event) {
                        return;
                    }

                    const classes = [
                        'no-body-padding',
                        'small-close-button',
                        'student-network-event-details',
                        'clickable-app-header-menu',
                    ];

                    if (event.anonymized) classes.push('anonymized');

                    reactRouterBridge.search('event-id', event.id);
                    DialogModal.alert({
                        content:
                            '<student-network-event-details event="event" container-selector="containerSelector"></student-network-event-details>',
                        blurTargetSelector: '#app-main-container',
                        classes,
                        animated: false,
                        closeOnOutsideClick: true,
                        close: () => {
                            reactRouterBridge.search('event-id', null);
                        },
                        scope: {
                            event,
                            containerSelector: 'dialog-modal-alert .modal-content',
                        },
                    });
                },

                //--------------------
                // Map Interactions
                //--------------------

                _onIdleEvent() {
                    if (!this.activeMapLayer.supportsRefreshOnIdleMapEvent) {
                        return;
                    }

                    this.activeMapLayer.refresh();
                },

                //--------------------
                // Map Resizing
                //--------------------

                _updateMapSize() {
                    const center = this.map.getCenter();
                    googleMaps.event.trigger(this.map, 'resize');
                    this.map.setCenter(center);
                },
            };
        });

        return StudentNetworkMapViewModel;
    },
]);
